import { gql, TypedDocumentNode } from "@apollo/client";

import {
  Maybe,
  UserGuide,
  UserGuideGroup,
  UserGuideChapter,
  UserGuideMetadata,
  UserGuideProgress,
  UserGuideStep,
  UserGuideCompletion,
  QueryUserGuideGroupArgs,
  MutationUserGuideStartArgs,
  MutationUserGuideRestartArgs,
} from "types/generated";

type FetchUserGuideGroupsData = {
  userGuideGroups: (Pick<UserGuideGroup, "id" | "name" | "description" | "skillLevel" | "order"> & {
    chapters: Array<{
      guides: Array<
        Pick<UserGuide, "id"> & { metadata: Pick<UserGuideMetadata, "minutesToComplete"> }
      >;
    }>;
  })[];
  completedUserGuides: Array<{
    guide: Pick<UserGuide, "id">;
  }>;
};

export const GET_USER_GUIDE_GROUPS: TypedDocumentNode<FetchUserGuideGroupsData, void> = gql`
  query GetUserGuideGroups {
    userGuideGroups {
      id
      name
      description
      skillLevel
      order
      chapters {
        guides {
          id
          metadata {
            minutesToComplete
          }
        }
      }
    }
    completedUserGuides {
      guide {
        id
      }
    }
  }
`;

type FetchUserGuideGroupData = {
  userGuideGroup: Maybe<
    Pick<UserGuideGroup, "id" | "name" | "description" | "skillLevel"> & {
      chapters: Array<
        Pick<UserGuideChapter, "id" | "name" | "description"> & {
          guides: Array<
            Pick<UserGuide, "id"> & {
              metadata: Pick<
                UserGuideMetadata,
                "title" | "description" | "prerequisites" | "minutesToComplete"
              >;
              prerequisiteGuides: {
                all: Array<Pick<UserGuide, "id">>;
                completed: Array<Pick<UserGuide, "id">>;
              };
            }
          >;
        }
      >;
    }
  >;
  activeUserGuideProgress: Maybe<{
    guide: Pick<UserGuide, "id">;
  }>;
  completedUserGuides: Array<{
    guide: Pick<UserGuide, "id">;
  }>;
};

export const GET_USER_GUIDE_GROUP: TypedDocumentNode<
  FetchUserGuideGroupData,
  QueryUserGuideGroupArgs
> = gql`
  query GetUserGuideGroup($id: ID!) {
    userGuideGroup(id: $id) {
      id
      name
      description
      skillLevel
      chapters {
        id
        name
        description
        guides {
          id
          metadata {
            title
            description
            prerequisites
            minutesToComplete
          }
          prerequisiteGuides {
            all {
              id
            }
            completed {
              id
            }
          }
        }
      }
    }
    activeUserGuideProgress {
      guide {
        id
      }
    }
    completedUserGuides {
      guide {
        id
      }
    }
  }
`;

export type UserGuideStepData = Pick<
  UserGuideStep,
  "id" | "title" | "instruction" | "hint" | "order"
>;

type FetchUserGuideProgressData = {
  activeUserGuideProgress: Maybe<{
    guide: Pick<UserGuide, "id"> & {
      metadata: Pick<UserGuideMetadata, "title" | "description" | "prerequisites">;
      steps: Array<UserGuideStepData>;
      completion: UserGuideCompletion;
    };
    currentStep: UserGuideProgress["currentStep"];
  }>;
  completedUserGuides: Array<{
    guide: Pick<UserGuide, "id">;
  }>;
};

export const GET_USER_GUIDE_PROGRESS: TypedDocumentNode<FetchUserGuideProgressData, void> = gql`
  query GetUserGuideProgress {
    activeUserGuideProgress {
      guide {
        id
        metadata {
          title
          description
          prerequisites
        }
        steps {
          id
          title
          instruction
          hint
          order
        }
        completion {
          recommendedGuideIds
          successMessage
        }
      }
      currentStep
    }
    completedUserGuides {
      guide {
        id
      }
    }
  }
`;

type UserGuideStartData = {
  userGuideStart: Pick<UserGuideProgress, "status">;
};

export const USER_GUIDE_START: TypedDocumentNode<UserGuideStartData, MutationUserGuideStartArgs> =
  gql`
    mutation UserGuideStart($id: ID!) {
      userGuideStart(id: $id) {
        status
      }
    }
  `;

type UserGuideMoveToNextStepData = {
  userGuideMoveToNextStep: Pick<UserGuideProgress, "status">;
};

export const USER_GUIDE_MOVE_TO_NEXT_STEP: TypedDocumentNode<UserGuideMoveToNextStepData, void> =
  gql`
    mutation UserGuideMoveToNextStep {
      userGuideMoveToNextStep {
        status
      }
    }
  `;

type UserGuideCompleteData = {
  userGuideComplete: Pick<UserGuideProgress, "status">;
};

export const USER_GUIDE_COMPLETE: TypedDocumentNode<UserGuideCompleteData, void> = gql`
  mutation UserGuideComplete {
    userGuideComplete {
      status
    }
  }
`;

type UserGuideAbandonData = {
  userGuideAbandon: Pick<UserGuideProgress, "status">;
};

export const USER_GUIDE_ABANDON: TypedDocumentNode<UserGuideAbandonData, void> = gql`
  mutation UserGuideAbandon {
    userGuideAbandon {
      status
    }
  }
`;

type UserGuideRestartData = {
  userGuideRestart: Pick<UserGuideProgress, "status">;
};

export const USER_GUIDE_RESTART: TypedDocumentNode<
  UserGuideRestartData,
  MutationUserGuideRestartArgs
> = gql`
  mutation UserGuideRestart($id: ID!) {
    userGuideRestart(id: $id) {
      status
    }
  }
`;
